// The inventory reference and worksheet use the same unit defaults and existing formula.
// Copies in backend/frontend are checked byte-for-byte by the release tests.
const { homeClassification } = require('./homeClassification');
const { worksheetMaximum } = require('./worksheetMaximum');
const { DEFAULT_STEP_SETS, defaultStepsAmount } = require('./worksheetDefaults');
const { SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE, SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE } = require('./worksheetAllowances');
// Helper to safely read nested values
const get = (obj, path, fallback) => {
    if (!obj || !path) return fallback;
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
        if (current && Object.prototype.hasOwnProperty.call(current, part)) {
            current = current[part];
        } else {
            return fallback;
        }
    }
    return current ?? fallback;
};

/** Read nested value when the path exists; `found: false` if any segment is missing. */
const getRaw = (obj, path) => {
    if (!obj || !path) return { found: false, value: undefined };
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
        if (current != null && Object.prototype.hasOwnProperty.call(current, part)) {
            current = current[part];
        } else {
            return { found: false, value: undefined };
        }
    }
    return { found: true, value: current };
};

function getSkirtingAllowanceByType(skirtingType, isSingleWide) {
    if (!skirtingType || isSingleWide == null) return null;
    const data = isSingleWide ? SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE : SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE;
    const row = data.find((r) => r.item && r.item === skirtingType);
    return row ? row.max : null;
}

// Get max allowance by item name from Single or Double/Triple Wide table.
function getAllowanceByItemName(itemName, isSingleWide) {
    if (isSingleWide == null) return null;
    const data = isSingleWide ? SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE : SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE;
    const row = data.find((r) => r.item && r.item === itemName);
    return row ? row.max : null;
}


function buildPricePrefill(inventory = {}) {
    const sections = homeClassification(inventory).sections;
    const isSingleWide = sections == null ? null : sections === 1;
    const g = (path, defaultValue) => {
        const { found, value } = getRaw(inventory, path);
        if (!found || value === undefined) return defaultValue;
        if (value === null || value === '') return '';
        return value;
    };
    const numberOfSets = parseInt(g('maxSalesPriceCalculatorAddFields.numberOfSets', DEFAULT_STEP_SETS), 10) || 0;
    return {
        // MaxSalesPrice section
        invoice: get(inventory, 'invoice', undefined) ?? '',
        freight: g('freight', get(inventory, 'maxSalesPriceCalculatorDeletedFields.factoryFreight', undefined)) ?? '',
        stateTax: g('stateTax', get(inventory, 'maxSalesPriceCalculatorDeletedFields.taxFromInvoice', undefined)) ?? '',
        vepCode: ((String(inventory.vepCode ?? get(inventory, 'inventoryUnit.vepCode', '')).trim()) || '0'),

        // Deletions — Wheels & Axles: 500 (single) / 1000 (double-triple) when null/undefined
        wheelsAndAxles: g('maxSalesPriceCalculatorDeletedFields.wheelsAndAxles', isSingleWide == null ? '' : isSingleWide ? 500 : 1000),
        airConditioner: g('maxSalesPriceCalculatorDeletedFields.airConditioner', ''),
        hudDuesFees: g('maxSalesPriceCalculatorDeletedFields.hudDuesFees', ''),
        furnitureEntertainmentPack: g('maxSalesPriceCalculatorDeletedFields.furnitureEntertainmentPack', ''),
        otherPacksSalesAllowances: g('maxSalesPriceCalculatorDeletedFields.otherPacksSalesAllowances', ''),
        dealerRebate: g('maxSalesPriceCalculatorDeletedFields.dealerRebate', ''),
        advertisingEnvironmentalAllowance: g('maxSalesPriceCalculatorDeletedFields.advertisingEnvironmentalAllowance', ''),
        trimOutTapeAndTexture: g('maxSalesPriceCalculatorDeletedFields.trimOutTapeAndTexture', ''),
        otherDeletion: g('maxSalesPriceCalculatorDeletedFields.other', ''),
        taxFromInvoice: g('maxSalesPriceCalculatorDeletedFields.taxFromInvoice', inventory.stateTax) ?? '',

        // Adds — from unit or allowance table (get(..., undefined) ?? getAllowanceByItemName(...) or '')
        addFactoryFreight: g('maxSalesPriceCalculatorAddFields.factoryFreight', inventory.freight) ?? '',
        addAirConditioner: g('maxSalesPriceCalculatorAddFields.airConditioner', getAllowanceByItemName('Air Conditioner', isSingleWide)) ?? '',
        addTaxFromInvoice: g('maxSalesPriceCalculatorAddFields.taxFromInvoice', inventory.stateTax) ?? '',
        addHudDuesFees: g('maxSalesPriceCalculatorAddFields.hudDuesFees', 350),
        addDeliveryAndSet: g('maxSalesPriceCalculatorAddFields.deliveryAndSet', inventory.deliveryAndSet ?? getAllowanceByItemName('Delivery & Set', isSingleWide)) ?? '',
        addSkirtingType: g('maxSalesPriceCalculatorAddFields.skirtingType', ''),
        addSkirting: (() => {
            const stored = getRaw(inventory, 'maxSalesPriceCalculatorAddFields.skirting');
            if (stored.found) {
                return stored.value === null || stored.value === '' ? '' : String(stored.value);
            }
            if (inventory.skirting != null && inventory.skirting !== '') return String(inventory.skirting);
            const allowance = getSkirtingAllowanceByType(g('maxSalesPriceCalculatorAddFields.skirtingType', ''), isSingleWide);
            return allowance != null ? String(allowance) : '';
        })(),
        addStepsDecks: g('maxSalesPriceCalculatorAddFields.stepsDecks', defaultStepsAmount(numberOfSets)),
        addNumberOfSets: g('maxSalesPriceCalculatorAddFields.numberOfSets', DEFAULT_STEP_SETS),
        addDirtPad: g('maxSalesPriceCalculatorAddFields.dirtPad', inventory.dirtPad ?? getAllowanceByItemName('Pad', isSingleWide)) ?? '',
        addFooters: g('maxSalesPriceCalculatorAddFields.footers', inventory.footers ?? getAllowanceByItemName('Footers', isSingleWide)) ?? '',
        addTrimOutTapeAndTexture: g('maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture', getAllowanceByItemName('Trim Out', isSingleWide)) ?? '',
        addElectricHookup: g('maxSalesPriceCalculatorAddFields.electricHookup', getAllowanceByItemName('Electric Hookups', isSingleWide)) ?? '',
        addWaterHookup: g('maxSalesPriceCalculatorAddFields.waterHookup', getAllowanceByItemName('Water Hookups', isSingleWide)) ?? '',
        addSewerHookup: g('maxSalesPriceCalculatorAddFields.sewerHookup', getAllowanceByItemName('Sewer Hookups', isSingleWide)) ?? '',
        addGasHookup: g('maxSalesPriceCalculatorAddFields.gasHookup', getAllowanceByItemName('Gas Hookups', isSingleWide)) ?? '',
        addGuttersAndDownspouts: g('maxSalesPriceCalculatorAddFields.guttersAndDownspouts', inventory.guttersDownspouts ?? getAllowanceByItemName('Gutters & Downspouts', isSingleWide)) ?? '',
        addOther: g('maxSalesPriceCalculatorAddFields.other', inventory.otherAdds) ?? '',
        addOtherAmount: g('maxSalesPriceCalculatorAddFields.otherAmount', ''),

        isSingleWide,
        numberOfSections: inventory.numberOfSections ?? '',

        maxSalesPriceSalesTax: g('maxSalesPriceSalesTax', ''),

    };
}
const number = value => value == null || value === '' ? 0 : Number(value);
const deletionKeys = {
 wheelsAndAxles: 'wheelsAndAxles', factoryFreight: 'freight', airConditioner: 'airConditioner',
 taxFromInvoice: 'taxFromInvoice', hudDuesFees: 'hudDuesFees', furnitureEntertainmentPack: 'furnitureEntertainmentPack',
 otherPacksSalesAllowances: 'otherPacksSalesAllowances', dealerRebate: 'dealerRebate',
 advertisingEnvironmentalAllowance: 'advertisingEnvironmentalAllowance', trimOutTapeAndTexture: 'trimOutTapeAndTexture', other: 'otherDeletion'
};
const additionKeys = {
 factoryFreight: 'addFactoryFreight', airConditioner: 'addAirConditioner', taxFromInvoice: 'addTaxFromInvoice',
 hudDuesFees: 'addHudDuesFees', deliveryAndSet: 'addDeliveryAndSet', skirting: 'addSkirting', stepsDecks: 'addStepsDecks',
 dirtPad: 'addDirtPad', footers: 'addFooters', trimOutTapeAndTexture: 'addTrimOutTapeAndTexture',
 electricHookup: 'addElectricHookup', waterHookup: 'addWaterHookup', sewerHookup: 'addSewerHookup',
 gasHookup: 'addGasHookup', guttersAndDownspouts: 'addGuttersAndDownspouts', otherAmount: 'addOtherAmount'
};
function getInventoryMaximum(unit = {}) {
    const saved = unit.maxSalesPrice;
    if (String(unit.type || '').trim().toLowerCase() !== 'new') {
        const valid = saved !== null && saved !== undefined && saved !== '' && Number.isFinite(Number(saved)) && Number(saved) >= 0;
        return { value: valid ? Number(saved) : null, source: valid ? 'saved' : 'unavailable', reason: valid ? 'Saved unit maximum' : 'No saved maximum. Open the worksheet to review this unit.' };
    }
    const invalid = reason => ({ value: null, source: 'unavailable', reason });
    if (![1,2,3].includes(homeClassification(unit).sections)) return invalid('Review the unit section count, serials and overall size.');
    const p = buildPricePrefill(unit);
    if (!Number.isFinite(number(p.invoice)) || number(p.invoice) <= 0) return invalid('Enter a positive invoice amount in the worksheet.');
    if (!['0','1','2'].includes(String(p.vepCode).trim())) return invalid('Review the worksheet VEP code.');
    const values = keys => Object.fromEntries(Object.entries(keys).map(([key,field]) => [key, number(p[field])]));
    const inputs = { invoice: number(p.invoice), vepCode: p.vepCode, maxSalesPriceSalesTax: number(p.maxSalesPriceSalesTax),
        maxSalesPriceCalculatorDeletedFields: values(deletionKeys), maxSalesPriceCalculatorAddFields: values(additionKeys) };
    const amounts = [...Object.values(inputs.maxSalesPriceCalculatorDeletedFields), ...Object.values(inputs.maxSalesPriceCalculatorAddFields), inputs.maxSalesPriceSalesTax];
    if (amounts.some(value => !Number.isFinite(value) || value < 0)) return invalid('Review the worksheet amounts.');
    const totals = worksheetMaximum(inputs);
    if (totals.newHomeNetInvoice < 0 || !Number.isFinite(totals.maximumSalesPriceOfHome) || totals.maximumSalesPriceOfHome < 0) return invalid('Review the invoice and worksheet deletions.');
    return { value: Number(totals.maximumSalesPriceOfHome.toFixed(2)), source: 'worksheet', reason: 'Calculation worksheet: Maximum Sales Price of Home (G + H = I)' };
}
module.exports = { buildPricePrefill, getInventoryMaximum, getSkirtingAllowanceByType, getAllowanceByItemName };
